import { kickCommand, statusQuery, decodeStatus } from "./escpos";
import {
  DrawerTransportError,
  type ConnectionState,
  type DrawerTransport,
  type PeripheralDevice,
} from "./transport";

/**
 * Web Bluetooth transport for the drawer.
 *
 * READ THIS BEFORE BUYING HARDWARE. Web Bluetooth speaks BLE (GATT) and ONLY
 * BLE. A large share of cheap thermal printers are Bluetooth CLASSIC using the
 * Serial Port Profile, and no browser on any platform can reach those — not
 * Chrome on Android, not desktop. Such a printer will either never appear in
 * the chooser, or appear and expose no writable characteristic, which surfaces
 * here as `permission_lost` / `disconnected` rather than as a crash. USB
 * remains the transport the plan is built on (research.md R15); this is for
 * units that genuinely expose a BLE serial service.
 *
 * THE GRANT IS WEAKER THAN USB'S. WebUSB's `getDevices()` reliably returns
 * previously granted devices, which is what lets a terminal reconnect silently
 * after a reload. The Bluetooth equivalent, `navigator.bluetooth.getDevices()`,
 * is not available in every Chrome build, so a terminal may need re-pairing —
 * a user gesture — after a restart. That is reported as `permission_lost`
 * (fixable in settings) rather than as a hardware fault, because the two need
 * different recovery and sending staff to check a cable would waste their time
 * (FR-105).
 *
 * NO SERVER ROUND TRIP IS ON THIS PATH, exactly as with USB: a drawer must
 * release during a total outage (FR-101).
 */

/**
 * Serial-over-GATT services used by BLE ESC/POS printers.
 *
 * Web Bluetooth will not let a page touch a service it did not declare up
 * front, so an unlisted printer cannot work no matter what it exposes — hence a
 * list rather than a single UUID. Characteristics are NOT hardcoded per
 * service: the transport enumerates and picks by PROPERTY (writable / notify),
 * because the same service id appears on different units with different
 * characteristic layouts, and a lookup table would become a hardware
 * compatibility matrix nobody maintains.
 */
const PRINTER_SERVICES: Array<number | string> = [
  0x18f0, // Most common BLE ESC/POS service; write characteristic is usually 0x2af1.
  0xff00, // Write 0xff02, notify 0xff01 on many Zjiang/Xprinter units.
  0xffe0, // HM-10 style transparent serial; single characteristic 0xffe1.
  0xae30, // GOOJPRT / some Zjiang firmware.
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip ISSC transparent UART.
];

/**
 * Bytes per GATT write.
 *
 * The default ATT MTU is 23, leaving 20 bytes of payload, and negotiating
 * higher is not something a page can rely on. The kick command is 5 bytes and
 * fits in one write; chunking exists because FR-108 has a receipt-printing
 * module reusing this transport later, and a receipt is thousands of bytes.
 */
const CHUNK_BYTES = 20;

/**
 * Split a payload into MTU-sized pieces.
 *
 * Pure and exported so the split is testable on its own: the kick is 5 bytes
 * and would never exercise it, but the receipt module FR-108 anticipates sends
 * thousands, and a boundary bug there prints garbage rather than failing.
 */
export function chunks(bytes: Uint8Array, size: number = CHUNK_BYTES): Uint8Array[] {
  const pieces: Uint8Array[] = [];
  for (let offset = 0; offset < bytes.length; offset += size) {
    pieces.push(bytes.slice(offset, offset + size));
  }
  return pieces;
}

/** A kick that never answers must not hang the till behind the sale. */
const KICK_TIMEOUT_MS = 3_000;

/**
 * Bound for a generic write — a ticket, not a 5-byte kick. Matches
 * SC-202's 15 s budget for a forty-line ticket, the case this bounds: at
 * 20 bytes per GATT write, a long ticket takes noticeably longer than the
 * cabled path (research.md R3, "Bluetooth is slower than a cable").
 */
const WRITE_TIMEOUT_MS = 15_000;

/**
 * How long to wait for a status notification.
 *
 * Deliberately short. A printer that has not answered in a second is a printer
 * that does not implement the real-time status query, and the answer to that is
 * "unknown" rather than a retry loop on the polling path.
 */
const STATUS_TIMEOUT_MS = 1_000;

interface BluetoothLike {
  requestDevice(options: {
    acceptAllDevices?: boolean;
    optionalServices?: Array<number | string>;
  }): Promise<BluetoothDeviceLike>;
  getDevices?: () => Promise<BluetoothDeviceLike[]>;
}

interface BluetoothDeviceLike {
  name?: string;
  gatt?: BluetoothServerLike;
  addEventListener(type: "gattserverdisconnected", listener: () => void): void;
  removeEventListener(type: "gattserverdisconnected", listener: () => void): void;
}

interface BluetoothServerLike {
  connected: boolean;
  connect(): Promise<BluetoothServerLike>;
  disconnect(): void;
  getPrimaryServices(): Promise<BluetoothServiceLike[]>;
}

interface BluetoothServiceLike {
  getCharacteristics(): Promise<BluetoothCharacteristicLike[]>;
}

interface BluetoothCharacteristicLike {
  properties: {
    write?: boolean;
    writeWithoutResponse?: boolean;
    notify?: boolean;
    indicate?: boolean;
  };
  writeValueWithResponse?: (value: BufferSource) => Promise<void>;
  writeValueWithoutResponse?: (value: BufferSource) => Promise<void>;
  writeValue?: (value: BufferSource) => Promise<void>;
  startNotifications(): Promise<BluetoothCharacteristicLike>;
  stopNotifications(): Promise<BluetoothCharacteristicLike>;
  addEventListener(type: "characteristicvaluechanged", listener: (event: Event) => void): void;
  removeEventListener(type: "characteristicvaluechanged", listener: (event: Event) => void): void;
}

function bluetooth(): BluetoothLike | null {
  return (navigator as unknown as { bluetooth?: BluetoothLike }).bluetooth ?? null;
}

export function isSupported(): boolean {
  return bluetooth() !== null;
}

export class WebBluetoothDrawerTransport implements DrawerTransport {
  #device: BluetoothDeviceLike | null = null;
  #writer: BluetoothCharacteristicLike | null = null;
  #notifier: BluetoothCharacteristicLike | null = null;
  /** Set while a status query is in flight; resolved by the notification. */
  #pendingStatus: ((byte: number | null) => void) | null = null;
  #onDisconnected: (() => void) | null = null;

  isSupported(): boolean {
    return isSupported();
  }

  /**
   * Prompt for a device.
   *
   * `acceptAllDevices` rather than a service filter, and that is deliberate:
   * most of these printers advertise only a name and expose their serial
   * service after connecting, so filtering on service id hides exactly the
   * hardware we are looking for. The cost is that the chooser lists every BLE
   * device in range — acceptable in a one-off settings action, and the
   * `optionalServices` list still bounds what the page may touch afterwards.
   */
  async requestPermission(): Promise<PeripheralDevice> {
    const bridge = bluetooth();
    if (!bridge) {
      throw new DrawerTransportError(
        "unsupported_browser",
        "This browser cannot talk to Bluetooth devices.",
      );
    }

    const device = await bridge.requestDevice({
      acceptAllDevices: true,
      optionalServices: PRINTER_SERVICES,
    });

    this.#device = device;
    return { name: device.name ?? "Bluetooth printer" };
  }

  /**
   * Open a device that has already been chosen.
   *
   * Falls back to `getDevices()` for a grant that survived a reload, and
   * reports `permission_lost` when there is nothing to reconnect to — including
   * on the Chrome builds where `getDevices` does not exist at all. The recovery
   * is the same in both cases: pair again from settings.
   */
  async connect(): Promise<ConnectionState> {
    const bridge = bluetooth();
    if (!bridge) return "disconnected";

    let device = this.#device;
    if (!device) {
      const granted = (await bridge.getDevices?.()) ?? [];
      device = granted[0] ?? null;
    }
    if (!device) return "permission_lost";

    const gatt = device.gatt;
    if (!gatt) return "disconnected";

    try {
      const server = gatt.connected ? gatt : await gatt.connect();
      const [writer, notifier] = await this.#discover(server);
      if (!writer) return "disconnected";

      this.#device = device;
      this.#writer = writer;
      this.#notifier = notifier;

      // BLE links drop on their own — idle timeout, range, the printer napping.
      // Without this the transport would keep reporting "connected" and every
      // kick would fail as a timeout instead of as a disconnect, which sends
      // staff looking for the wrong problem.
      this.#onDisconnected = () => {
        this.#writer = null;
        this.#notifier = null;
        this.#settleStatus(null);
      };
      device.addEventListener("gattserverdisconnected", this.#onDisconnected);

      if (notifier) await this.#listen(notifier);

      return "connected";
    } catch {
      return "disconnected";
    }
  }

  /** Pick characteristics by property, never by a per-model UUID table. */
  async #discover(
    server: BluetoothServerLike,
  ): Promise<[BluetoothCharacteristicLike | null, BluetoothCharacteristicLike | null]> {
    let writer: BluetoothCharacteristicLike | null = null;
    let notifier: BluetoothCharacteristicLike | null = null;

    const services = await server.getPrimaryServices();
    for (const service of services) {
      let characteristics: BluetoothCharacteristicLike[];
      try {
        characteristics = await service.getCharacteristics();
      } catch {
        // A service that refuses enumeration is not fatal; another may carry
        // the serial characteristic we actually need.
        continue;
      }

      for (const characteristic of characteristics) {
        const { write, writeWithoutResponse, notify, indicate } = characteristic.properties;
        if (!writer && (write || writeWithoutResponse)) writer = characteristic;
        if (!notifier && (notify || indicate)) notifier = characteristic;
      }
    }

    return [writer, notifier];
  }

  async #listen(notifier: BluetoothCharacteristicLike): Promise<void> {
    notifier.addEventListener("characteristicvaluechanged", (event) => {
      const value = (event.target as unknown as { value?: DataView }).value;
      if (!value || value.byteLength === 0) return;
      this.#settleStatus(value.getUint8(0));
    });
    await notifier.startNotifications();
  }

  #settleStatus(byte: number | null): void {
    const pending = this.#pendingStatus;
    this.#pendingStatus = null;
    pending?.(byte);
  }

  isConnected(): boolean {
    return this.#writer !== null && (this.#device?.gatt?.connected ?? false);
  }

  /**
   * Whether drawer state is readable.
   *
   * Requires a notify characteristic, because a BLE printer answers the
   * real-time status query through notifications rather than a readable value.
   * No notify characteristic means the alert is ABSENT, not guessed (FR-106).
   */
  hasDrawerStatus(): boolean {
    return this.#notifier !== null;
  }

  /**
   * Pulse the kick-out connector.
   *
   * Bounded by the same timeout as USB and for the same reason: a printer that
   * accepts the write and never completes it would otherwise leave a staff
   * member watching a spinner with the customer's money already in the drawer.
   */
  async kick(pin: 2 | 5 = 2): Promise<void> {
    if (!this.#writer || !this.isConnected()) {
      throw new DrawerTransportError("device_disconnected", "No drawer device is connected.");
    }

    await this.#withTimeout(
      this.#write(this.#writer, kickCommand(pin)),
      KICK_TIMEOUT_MS,
      new DrawerTransportError("kick_timeout", "The drawer did not respond."),
    );
  }

  /**
   * Send arbitrary bytes — a ticket. Same chunked writer as `kick()`, bounded
   * by the longer `WRITE_TIMEOUT_MS` because this is thousands of bytes at
   * 20 per GATT write, not five in one.
   */
  async write(bytes: Uint8Array): Promise<void> {
    if (!this.#writer || !this.isConnected()) {
      throw new DrawerTransportError("device_disconnected", "No drawer device is connected.");
    }

    await this.#withTimeout(
      this.#write(this.#writer, bytes),
      WRITE_TIMEOUT_MS,
      new DrawerTransportError("kick_timeout", "The printer did not respond."),
    );
  }

  /**
   * Write in MTU-sized chunks, sequentially.
   *
   * Sequential rather than parallel: GATT rejects a second write while one is
   * outstanding, and a printer that receives ESC/POS bytes out of order prints
   * (or kicks) garbage. Prefers the acknowledged write so a failure is visible
   * — a fire-and-forget write resolves even when nothing is listening.
   */
  async #write(characteristic: BluetoothCharacteristicLike, bytes: Uint8Array): Promise<void> {
    const acknowledged = characteristic.properties.write;
    for (const chunk of chunks(bytes)) {
      if (acknowledged && characteristic.writeValueWithResponse) {
        await characteristic.writeValueWithResponse(chunk as BufferSource);
      } else if (characteristic.writeValueWithoutResponse) {
        await characteristic.writeValueWithoutResponse(chunk as BufferSource);
      } else if (characteristic.writeValue) {
        // Older Chrome builds expose only the deprecated combined method.
        await characteristic.writeValue(chunk as BufferSource);
      } else {
        throw new DrawerTransportError(
          "device_disconnected",
          "The paired device exposes no writable characteristic.",
        );
      }
    }
  }

  /**
   * Read drawer state, ONLY where the device supports it.
   *
   * Returns null rather than a guess on every failure path — unsupported, timed
   * out, disconnected mid-read. A wrong "drawer left open" alert is worse than
   * no alert, because staff learn to ignore it and then ignore the real one
   * (FR-106).
   */
  async readDrawerState(): Promise<"open" | "closed" | null> {
    if (!this.#writer || !this.#notifier || !this.isConnected()) return null;

    try {
      const answered = new Promise<number | null>((resolve) => {
        this.#pendingStatus = resolve;
        setTimeout(() => this.#settleStatus(null), STATUS_TIMEOUT_MS);
      });

      await this.#write(this.#writer, statusQuery());
      const byte = await answered;
      if (byte === null) return null;
      return decodeStatus(byte).drawerOpen ? "open" : "closed";
    } catch {
      this.#settleStatus(null);
      return null;
    }
  }

  async #withTimeout<T>(work: Promise<T>, ms: number, failure: Error): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        work,
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(failure), ms);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  async disconnect(): Promise<void> {
    const device = this.#device;
    const notifier = this.#notifier;
    const listener = this.#onDisconnected;

    this.#writer = null;
    this.#notifier = null;
    this.#onDisconnected = null;
    this.#settleStatus(null);

    if (!device) return;
    try {
      if (listener) device.removeEventListener("gattserverdisconnected", listener);
      await notifier?.stopNotifications();
      device.gatt?.disconnect();
    } catch {
      // Already gone. Nothing to release, and nothing worth telling staff.
    }
  }
}
